import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightFromBracket, faChartLine, faHouse, faMoon, faSun, faUserGraduate, faUsers } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "../../useTheme";
import ZenLogoWhite from "../../imgs/ZenLogo_white.svg";
import "./Header.css";

const VERSION = "v1.2.5";

const Header = ({ data, socket } : { data: any, socket: any }) => {

    const navigate = useNavigate();
    const location = useLocation();
    const [count, setCount] = useState<number>(0);
    const [cookies, setCookie, removeCookie] = useCookies();
    const [theme, toggleTheme] = useTheme();
    const en = cookies?.language === "en";

    useEffect(() => {
        // Not every page that renders the header owns a socket.
        socket?.on("update-sessions", (sessions : any) => {
            setCount(currCount => currCount + 0.5);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const removeCookies = async () => {
        // The auth token is HttpOnly, so it can only be cleared server-side.
        try {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, { method: "POST" });
        } catch (e) { /* ignore */ }
        Object.keys(cookies).forEach((cookie : string) => removeCookie(cookie));
        window.location.replace("/login");
    };

    // Navigation entries, filtered by what the account is allowed to see. The
    // pages are reached with a full page load (as before) rather than a client
    // route change, so their data is fetched fresh.
    const links = [
        { to: "/categories", icon: faHouse, label: en ? "Home" : "Acasă", show: true },
        { to: "/students", icon: faUserGraduate, label: en ? "Students" : "Elevi", show: data.userData.type === "teacher" },
        { to: "/users", icon: faUsers, label: en ? "Users" : "Utilizatori", show: !!data.userData?.admin },
        { to: "/activity-log", icon: faChartLine, label: en ? "Activity" : "Activitate", show: !!data.userData?.admin },
    ].filter((l) => l.show);

    const isCurrent = (to: string) => location.pathname === to || location.pathname.startsWith(`${to}/`);

    return (
        <header className="app-header">
            <div className="ah-brand" onClick={() => window.location.replace("/categories")}>
                <img src={ZenLogoWhite} alt="ZenVolt" />
                <span className="ah-brand-name">ZenVolt</span>
                <span className="ah-version">{VERSION}</span>
            </div>

            <nav className="ah-nav">
                {links.map((l) => (
                    <button
                        key={l.to}
                        type="button"
                        className={`ah-link${isCurrent(l.to) ? " current" : ""}`}
                        aria-current={isCurrent(l.to) ? "page" : undefined}
                        onClick={() => window.location.replace(l.to)}
                    >
                        <FontAwesomeIcon icon={l.icon} />
                        <span>{l.label}</span>
                    </button>
                ))}
            </nav>

            <div className="ah-actions">
                <button
                    type="button"
                    className="ah-icon-btn ah-lang"
                    title={en ? "Schimbă în română" : "Switch to English"}
                    onClick={() => setCookie("language", en ? "ro" : "en")}
                >
                    {en ? "RO" : "EN"}
                </button>

                <button
                    type="button"
                    className="ah-icon-btn"
                    title={theme === "dark" ? (en ? "Light mode" : "Mod luminos") : (en ? "Dark mode" : "Mod întunecat")}
                    aria-label={theme === "dark" ? "Light mode" : "Dark mode"}
                    onClick={() => toggleTheme()}
                >
                    <FontAwesomeIcon icon={theme === "dark" ? faSun : faMoon} />
                </button>

                <button type="button" className="ah-logout" onClick={() => removeCookies()}>
                    <FontAwesomeIcon icon={faArrowRightFromBracket} />
                    <span>{en ? "Log out" : "Delogare"}</span>
                </button>
            </div>
        </header>
    );
};

export default Header;
